import React, { useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import PageHeader from '../../PageHeader/PageHeader';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import MainGridArea from '../MainGridArea';
import NotificationAlert from '../../NotificationAlert';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { colors } from '../../../assets/styles/themes/variables';
import useResponsivePadding from '../../../utils/useResponsivePadding';

interface CollectionsAppLayoutProps {
  title: string;
  subtext?: string;
  body?: React.ReactNode;
  backgroundClick?: () => void;
  tabs?: React.ReactNode;
  headerButton?: React.ReactNode;
  isSearchPageContainer?: boolean;
  children?: React.ReactNode;
}

const CollectionsAppLayout: React.FC<CollectionsAppLayoutProps> = (props) => {
  const { notification, hideNotification } = useContext(NotificationContext);
  const contentPadding = useResponsivePadding();

  const memoizedNotification = useMemo(() => {
    if (!notification) return null;

    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1,  paddingX: contentPadding }}>
        <Box sx={{ position: 'relative' }}>
          <NotificationAlert
            message={notification.message}
            severity={notification.severity}
            onClose={hideNotification}
            alertSx={{ width: '100%' }}
          />
        </Box>
      </Box>
    );
  }, [notification, hideNotification]);

  return <Box data-testid='collections-app-layout-container'>
    {memoizedNotification}
    <Box 
      data-testid='collections-app-layout-header-container'
      sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: props.isSearchPageContainer ? contentPadding : spaces.md,
        paddingRight: props.isSearchPageContainer ? contentPadding : spaces.md,
        paddingTop: spaces.md,
        paddingBottom: spaces.md,
        height: '64px',  // Lets keep height consistent regardless of headerButton
        position: 'relative',
        backgroundColor: colors.neutral0,
      }}>
      <PageHeader
        text={props.title}
        subtext={props.subtext}
        sx={{
          color: palette.text.primary,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: spaces.xs,
          paddingY: 0,
        }}
      />
      { props.headerButton }
    </Box>
    { props.tabs }
    {props.children ? props.children : <MainGridArea
      body={props.body}
    />}
  </Box>;
};

export default CollectionsAppLayout;
